/**
 * SubmitReportModal Component
 * Modal for submitting a power report with payment method selection
 * Tailwind version using OathUI components
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  InformationCircleIcon,
  BuildingLibraryIcon,
  BanknotesIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import {
  submitReport,
  submitCombinedPayment,
} from '../../../../services/oathPowerReportService';
import { getPaymentAccounts } from '../../../../services/oathPaymentAccountService';
import { formatCurrencyWithSymbol } from '../utils';
import { formatAmountOnBlur } from '../utils/formatters';
import { getDefaultAccount } from '../utils/paymentAccountHelpers';
import usePermissions from '../../../../hooks/usePermissions';
import { useOathAuth } from '../../../../services/oathAuthContext';
import AccountSelect from '../components/AccountSelect';

const SubmitReportModal = ({
  isOpen,
  onClose,
  onBack = null,           // If provided AND there are batches, shows a Back button that returns to BatchSelectionModal.
  report = null,
  onSuccess = null,
  // Optional batch payment data from BatchSelectionModal
  selectedBatches = null,
  batchPaymentAmounts = null,
  batchesData = null
}) => {
  const { canAnyInAgency } = usePermissions();
  const { user } = useOathAuth();
  const currentUserId = user?.portalUserId;

  // Electronic payment is gated to the specific agency that owns the report,
  // satisfied by either the standard or sub-agency variant of the slug.
  const canAccessElectronicPayment = useMemo(
    () => canAnyInAgency(report?.agencyId, ['READ:ELECTRONIC-PAYMENT', 'READ:ELECTRONIC-PAYMENT:SUB-AGENCY']),
    [canAnyInAgency, report?.agencyId]
  );

  // True when the modal was opened from the combined report+batch flow.
  const hasBatches = selectedBatches && selectedBatches.size > 0;

  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [netBankAccountId, setNetBankAccountId] = useState('');
  const [bufBankAccountId, setBufBankAccountId] = useState('');
  // Check payment fields
  const [netCheckNumber, setNetCheckNumber] = useState('');
  const [netCheckAmount, setNetCheckAmount] = useState('');
  const [bufCheckNumber, setBufCheckNumber] = useState('');
  const [bufCheckAmount, setBufCheckAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [refreshingAccounts, setRefreshingAccounts] = useState(false);
  const [error, setError] = useState('');

  // Load payment accounts for ACH payment
  const loadBankAccounts = useCallback(async (isBackgroundRefresh = false) => {
    try {
      if (isBackgroundRefresh) {
        setRefreshingAccounts(true);
      } else {
        setLoadingAccounts(true);
      }
      const agencyId = report?.agencyId;
      console.log('[SubmitReportModal] Loading payment accounts for agencyId:', agencyId, 'agencyName:', report?.agencyName);

      if (!agencyId) {
        setError('Agency ID not found');
        return;
      }

      const result = await getPaymentAccounts(agencyId);
      if (result.success) {
        const activeAccounts = (result.accounts || []).filter(a => a.isActive);
        setBankAccounts(activeAccounts);

        if (isBackgroundRefresh) {
          // Preserve current selections if those accounts still exist
          setNetBankAccountId(prev => {
            const stillExists = activeAccounts.some(a => String(a.accountId) === String(prev));
            return stillExists ? prev : (getDefaultAccount(activeAccounts, 'NET')?.accountId || '');
          });
          setBufBankAccountId(prev => {
            const stillExists = activeAccounts.some(a => String(a.accountId) === String(prev));
            return stillExists ? prev : (getDefaultAccount(activeAccounts, 'BUF')?.accountId || '');
          });
        } else {
          // Auto-select default accounts for NET and BUF
          const netDefault = getDefaultAccount(activeAccounts, 'NET');
          const bufDefault = getDefaultAccount(activeAccounts, 'BUF');
          setNetBankAccountId(netDefault?.accountId || '');
          setBufBankAccountId(bufDefault?.accountId || '');
        }
      } else {
        setError('Failed to load payment accounts: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading payment accounts:', err);
      setError('Failed to load payment accounts: ' + err.message);
    } finally {
      if (isBackgroundRefresh) {
        setRefreshingAccounts(false);
      } else {
        setLoadingAccounts(false);
      }
    }
  }, [report?.agencyId]);

  // Load bank accounts when modal opens and ACH is selected
  useEffect(() => {
    if (isOpen && paymentMethod === 'ACH') {
      loadBankAccounts();
    }
  }, [isOpen, paymentMethod, loadBankAccounts]);

  // Refresh bank accounts when user returns to tab (e.g. after managing accounts in admin)
  useEffect(() => {
    if (!isOpen || paymentMethod !== 'ACH') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadBankAccounts(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOpen, paymentMethod, loadBankAccounts]);

  // Calculate batch payment totals (handles empty string values from partial payments)
  const batchTotals = useMemo(() => {
    if (!hasBatches || !batchPaymentAmounts) {
      return { net: 0, buf: 0, misc: 0, total: 0 };
    }
    let net = 0, buf = 0, misc = 0;
    selectedBatches.forEach(batchId => {
      const amounts = batchPaymentAmounts[batchId];
      if (amounts) {
        // Handle empty string as 0 for calculations
        const netVal = amounts.net === '' ? 0 : (amounts.net || 0);
        const bufVal = amounts.buf === '' ? 0 : (amounts.buf || 0);
        const miscVal = amounts.misc === '' ? 0 : (amounts.misc || 0);
        net += netVal;
        buf += bufVal;
        misc += miscVal;
      }
    });
    // Round to avoid floating point precision issues
    const roundCurrency = (val) => Math.round((val + Number.EPSILON) * 100) / 100;
    return {
      net: roundCurrency(net),
      buf: roundCurrency(buf),
      misc: roundCurrency(misc),
      total: roundCurrency(net + buf + misc)
    };
  }, [hasBatches, selectedBatches, batchPaymentAmounts]);

  // Calculate combined grand total
  const grandTotal = useMemo(() => {
    const reportTotal = (report?.totalNet || 0) + (report?.totalBuf || 0);
    return reportTotal + batchTotals.total;
  }, [report, batchTotals]);

  // Combined NET / BUF / MISC across the report and any selected batches.
  // These re-split the grandTotal by category so the breakdown actually
  // breaks down the headline, not just the batches portion.
  const combinedTotals = useMemo(() => ({
    net:  (report?.totalNet || 0) + batchTotals.net,
    buf:  (report?.totalBuf || 0) + batchTotals.buf,
    misc: batchTotals.misc, // report has no MISC bucket
  }), [report, batchTotals]);

  // Default to Check on each open; user can switch to ACH if they have access.
  useEffect(() => {
    if (isOpen) {
      setPaymentMethod('Check');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-populate check amounts when modal opens with Check as default.
  // When batches are attached, the displayed amount covers report + batch totals
  // (batch MISC rolls into the NET bucket, matching the backend split on submit).
  useEffect(() => {
    if (isOpen && report && paymentMethod === 'Check') {
      const netTotal = (report.totalNet || 0) + batchTotals.net + batchTotals.misc;
      const bufTotal = (report.totalBuf || 0) + batchTotals.buf;
      setNetCheckAmount(netTotal.toFixed(2));
      setBufCheckAmount(bufTotal.toFixed(2));
    }
  }, [isOpen, report, paymentMethod, batchTotals.net, batchTotals.buf, batchTotals.misc]);

  // Clear stale BUF check number when the BUF amount is zeroed out so the
  // disabled field doesn't carry a value into the submission payload.
  useEffect(() => {
    if (!parseFloat(bufCheckAmount) && bufCheckNumber) {
      setBufCheckNumber('');
    }
  }, [bufCheckAmount, bufCheckNumber]);

  // Handle payment method change
  const handlePaymentMethodChange = (newMethod) => {
    setPaymentMethod(newMethod);
    setError('');

    // Auto-populate check amounts when switching to Check payment
    if (newMethod === 'Check' && report) {
      const netTotal = (report.totalNet || 0) + batchTotals.net + batchTotals.misc;
      const bufTotal = (report.totalBuf || 0) + batchTotals.buf;
      setNetCheckAmount(netTotal.toFixed(2));
      setBufCheckAmount(bufTotal.toFixed(2));
    }
  };

  // Reset form state
  const resetForm = useCallback(() => {
    setPaymentMethod('Check');
    setNetBankAccountId('');
    setBufBankAccountId('');
    setNetCheckNumber('');
    setNetCheckAmount('');
    setBufCheckNumber('');
    setBufCheckAmount('');
    setRefreshingAccounts(false);
    setError('');
  }, []);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Validate form before submission
  const validateForm = () => {
    if (paymentMethod === 'ACH') {
      // Only require NET bank account if there's NET to pay
      const hasNetAmount = (report?.totalNet || 0) > 0 || (batchTotals.net || 0) > 0;
      if (hasNetAmount && !netBankAccountId) {
        setError('Please select a bank account for NET payment');
        return false;
      }
      // Only require BUF bank account if there's BUF to pay
      const hasBufAmount = (report?.totalBuf || 0) > 0 || (batchTotals.buf || 0) > 0;
      if (hasBufAmount && !bufBankAccountId) {
        setError('Please select a bank account for BUF payment');
        return false;
      }
    } else if (paymentMethod === 'Check') {
      const combinedNet = (report?.totalNet || 0) + batchTotals.net + batchTotals.misc;
      if (combinedNet > 0) {
        if (!netCheckAmount || parseFloat(netCheckAmount) <= 0) {
          setError('Please enter a valid NET check amount');
          return false;
        }
      }

      const combinedBuf = (report?.totalBuf || 0) + batchTotals.buf;
      if (combinedBuf > 0) {
        if (!bufCheckAmount || parseFloat(bufCheckAmount) <= 0) {
          setError('Please enter a valid BUF check amount');
          return false;
        }
      }

      // Check numbers are optional, but if both are provided they must differ
      // (NET and BUF need to be deposited as separate physical checks).
      const trimmedNet = netCheckNumber.trim();
      const trimmedBuf = bufCheckNumber.trim();
      if (trimmedNet && trimmedBuf && trimmedNet === trimmedBuf) {
        setError('NET and BUF check numbers must be different');
        return false;
      }
    }
    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      let response;

      if (hasBatches) {
        // Combined submission: report + batch payments in a single atomic transaction.
        // Both ACH and Check are supported. For Check, the displayed combined amount is
        // split across the backend's separate ER and batch fields, with the same check
        // number duplicated into both slots (one physical check per bucket).
        const batches = [];
        selectedBatches.forEach(batchId => {
          const amounts = batchPaymentAmounts[batchId];
          if (amounts) {
            // Handle empty string as 0 for submission
            const netAmount = amounts.net === '' ? 0 : (amounts.net || 0);
            const bufAmount = amounts.buf === '' ? 0 : (amounts.buf || 0);
            const miscAmount = amounts.misc === '' ? 0 : (amounts.misc || 0);
            batches.push({
              batchId: batchId,
              netAmount: netAmount,
              bufAmount: bufAmount,
              miscAmount: miscAmount,
              total: netAmount + bufAmount + miscAmount
            });
          }
        });

        const combinedSubmissionData = paymentMethod === 'ACH' ? {
          paymentMethod: 'ACH',
          netBankAccountId: parseInt(netBankAccountId),
          bufBankAccountId: parseInt(bufBankAccountId),
          batches: batches,
          batchTotalNet: batchTotals.net,
          batchTotalBuf: batchTotals.buf,
          batchTotalMisc: batchTotals.misc
        } : {
          paymentMethod: 'Check',
          // The NET/BUF check amount inputs are editable in the combined flow,
          // so the values the user actually wrote on the check are sent as
          // checkAmountCommission / checkAmountBuf. The batch fields below
          // still carry the derived batch portions for backend bookkeeping.
          checkNumberCommission: netCheckNumber,
          checkAmountCommission: parseFloat(netCheckAmount) || 0,
          checkNumberBuf:        bufCheckNumber,
          checkAmountBuf:        parseFloat(bufCheckAmount) || 0,
          batchCheckNumberNet:   netCheckNumber,
          batchCheckAmountNet:   batchTotals.net + batchTotals.misc,
          batchCheckNumberBuf:   bufCheckNumber,
          batchCheckAmountBuf:   batchTotals.buf,
          batches: batches,
          batchTotalNet: batchTotals.net,
          batchTotalBuf: batchTotals.buf,
          batchTotalMisc: batchTotals.misc
        };

        const submitResult = await submitCombinedPayment(report.powerReportId, combinedSubmissionData);
        if (!submitResult.success) {
          throw new Error(submitResult.error || 'Failed to submit combined payment');
        }

        response = submitResult.data;

        toast.success(
          `Report ${response.reportNumber} submitted with ${batches.length} batch payment(s)! Total: ${formatCurrencyWithSymbol(response.grandTotal || grandTotal)}`
        );
      } else {
        // Standard submission (no batches)
        const submissionData = {
          paymentMethod: paymentMethod,
          netBankAccountId: paymentMethod === 'ACH' ? parseInt(netBankAccountId) : null,
          bufBankAccountId: paymentMethod === 'ACH' ? parseInt(bufBankAccountId) : null,
          checkNumberCommission: paymentMethod === 'Check' ? netCheckNumber : null,
          checkAmountCommission: paymentMethod === 'Check' ? parseFloat(netCheckAmount) : null,
          checkNumberBuf: paymentMethod === 'Check' ? bufCheckNumber : null,
          checkAmountBuf: paymentMethod === 'Check' ? parseFloat(bufCheckAmount) : null
        };

        const submitResult = await submitReport(report.powerReportId, submissionData);
        if (submitResult.success) {
          response = submitResult.data;
        } else {
          throw new Error(submitResult.error || 'Failed to submit report');
        }
      }

      if (onSuccess) {
        onSuccess(response);
      }

      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      setError('Failed to submit report: ' + error.message);
      toast.error('Failed to submit report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!report) return null;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      title="Submit Power Report"
      size="2xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          {onBack && (
            <OathButton
              variant="outline"
              onClick={onBack}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Back
            </OathButton>
          )}
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            disabled={loading || (paymentMethod === 'ACH' && bankAccounts.length === 0)}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Submitting...
              </span>
            ) : (
              <>
                Submit Report
                <span className="mx-1.5 opacity-60">·</span>
                <span className="tabular-nums">
                  {formatCurrencyWithSymbol(hasBatches ? grandTotal : ((report?.totalNet || 0) + (report?.totalBuf || 0)))}
                </span>
              </>
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-3 sm:space-y-5">
        {/* Hero row: Amount Due (left, hero card) + Report Details (right, supporting card) */}
        <div className="grid gap-3 sm:gap-4 lg:grid-cols-5">
          {/* Amount Due — gradient hero with the headline number */}
          <div className="lg:col-span-3 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-700 via-primary to-primary-light text-white p-4 sm:p-6 shadow-lg">
            {/* Soft circle accent */}
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 pointer-events-none" />
            <div className="absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-white/5 pointer-events-none" />

            <p className="relative text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/70">
              {hasBatches ? 'Combined Amount Due' : 'Amount Due'}
            </p>
            <p className="relative text-3xl sm:text-5xl font-bold tracking-tight mb-3 sm:mb-4 break-words">
              {formatCurrencyWithSymbol(hasBatches ? grandTotal : ((report?.totalNet || 0) + (report?.totalBuf || 0)))}
            </p>

            {/* Breakdown — addends (Report + Batches) sum to the headline.
                The NET/BUF/MISC row is a re-split of the *entire* headline by
                category (combined across both report and batches), positioned
                as a sibling of the addends row to make it clear it breaks
                down the whole Combined Amount Due, not just batches. */}
            <div className="relative">
              {/* Mobile: stacked rows */}
              <div className="sm:hidden space-y-1.5 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Report</span>
                  <span className="font-semibold tabular-nums">
                    {formatCurrencyWithSymbol((report?.totalNet || 0) + (report?.totalBuf || 0))}
                  </span>
                </div>
                {hasBatches && (
                  <div className="flex justify-between gap-3">
                    <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">
                      + Batches&nbsp;<span className="text-white/70">×{selectedBatches.size}</span>
                    </span>
                    <span className="font-semibold tabular-nums">{formatCurrencyWithSymbol(batchTotals.total)}</span>
                  </div>
                )}
                {hasBatches && (combinedTotals.net > 0 || combinedTotals.buf > 0 || combinedTotals.misc > 0) && (
                  <div className="ml-3 pl-3 border-l-2 border-white/30 space-y-0.5 text-xs">
                    <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                      ↳ Breakdown
                    </div>
                    {combinedTotals.net > 0 && (
                      <div className="flex justify-between gap-3 text-white/75">
                        <span className="uppercase tracking-wider">NET</span>
                        <span className="tabular-nums">{formatCurrencyWithSymbol(combinedTotals.net)}</span>
                      </div>
                    )}
                    {combinedTotals.buf > 0 && (
                      <div className="flex justify-between gap-3 text-white/75">
                        <span className="uppercase tracking-wider">BUF</span>
                        <span className="tabular-nums">{formatCurrencyWithSymbol(combinedTotals.buf)}</span>
                      </div>
                    )}
                    {combinedTotals.misc > 0 && (
                      <div className="flex justify-between gap-3 text-white/75">
                        <span className="uppercase tracking-wider">MISC</span>
                        <span className="tabular-nums">{formatCurrencyWithSymbol(combinedTotals.misc)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* sm+: addends row + a peer breakdown row that re-splits the
                  whole headline (combined across report and batches) by category. */}
              <div className="hidden sm:block space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-full bg-white text-sm shadow-sm">
                    <span className="text-primary-700 text-xs uppercase tracking-wider font-semibold">Report</span>
                    <span className="text-primary-900 font-bold tabular-nums">
                      {formatCurrencyWithSymbol((report?.totalNet || 0) + (report?.totalBuf || 0))}
                    </span>
                  </span>
                  {hasBatches && (
                    <>
                      <span className="text-white/70 font-bold text-base">+</span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-sm shadow-sm">
                        <span className="text-primary-700 text-xs uppercase tracking-wider font-semibold">Batches</span>
                        <span className="text-primary-700 text-xs font-semibold tabular-nums">×{selectedBatches.size}</span>
                        <span className="text-primary-900 font-bold tabular-nums">{formatCurrencyWithSymbol(batchTotals.total)}</span>
                      </span>
                    </>
                  )}
                </div>
                {hasBatches && (combinedTotals.net > 0 || combinedTotals.buf > 0 || combinedTotals.misc > 0) && (
                  <div className="flex flex-wrap items-center gap-1.5 ml-3 pl-3 border-l-2 border-white/30">
                    <span className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">
                      ↳ Breakdown
                    </span>
                    {combinedTotals.net > 0 && (
                      <span className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-white/15 text-xs">
                        <span className="text-white/80 uppercase tracking-wider font-semibold">NET</span>
                        <span className="text-white tabular-nums">{formatCurrencyWithSymbol(combinedTotals.net)}</span>
                      </span>
                    )}
                    {combinedTotals.buf > 0 && (
                      <span className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-white/15 text-xs">
                        <span className="text-white/80 uppercase tracking-wider font-semibold">BUF</span>
                        <span className="text-white tabular-nums">{formatCurrencyWithSymbol(combinedTotals.buf)}</span>
                      </span>
                    )}
                    {combinedTotals.misc > 0 && (
                      <span className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-white/15 text-xs">
                        <span className="text-white/80 uppercase tracking-wider font-semibold">MISC</span>
                        <span className="text-white tabular-nums">{formatCurrencyWithSymbol(combinedTotals.misc)}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Report Details — quiet supporting card */}
          <div className="lg:col-span-2 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 sm:p-5 flex flex-col">
            <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400 mb-2">
              Report Details
            </p>
            <div className="mb-4 space-y-2">
              <div className="min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400">Agency</p>
                <p className="text-base font-bold text-gray-900 dark:text-white break-words sm:truncate" title={`${report.agencyName}${report.agencyId ? ` (${report.agencyId})` : ''}`}>
                  {report.agencyName}
                  {report.agencyId && (
                    <span className="ml-1 text-sm font-medium text-gray-500 dark:text-gray-400">({report.agencyId})</span>
                  )}
                </p>
              </div>
              {report.reportNumber && (
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Report</p>
                  <p className="font-semibold text-gray-900 dark:text-white break-all sm:break-normal sm:truncate" title={report.reportNumber}>
                    {report.reportNumber}
                  </p>
                </div>
              )}
            </div>
            {/* Mobile: each metric is a label/value ROW (sm:hidden). Desktop: 3-col grid (hidden sm:grid). */}

            <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              {[
                ['Powers',    report.powerCount],
                ['Liability', formatCurrencyWithSymbol(report.totalBondAmount)],
                ['Premium',   formatCurrencyWithSymbol(report.totalPremium)],
                ['NET',       formatCurrencyWithSymbol(report.totalNet)],
                ['BUF',       formatCurrencyWithSymbol(report.totalBuf)],
                ...(report.totalScamount > 0 ? [['SC Amount', formatCurrencyWithSymbol(report.totalScamount)]] : []),
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3 py-1.5">
                  <span className="text-gray-500 dark:text-gray-400">{label}</span>
                  <span className="font-semibold text-gray-900 dark:text-white tabular-nums">{value}</span>
                </div>
              ))}
            </div>

            <dl className="hidden sm:grid grid-cols-3 gap-x-4 gap-y-3 text-sm flex-1">
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Powers</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{report.powerCount}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Liability</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report.totalBondAmount)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">Premium</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report.totalPremium)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">NET</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report.totalNet)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">BUF</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report.totalBuf)}</dd>
              </div>
              {report.totalScamount > 0 && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">SC Amount</dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">{formatCurrencyWithSymbol(report.totalScamount)}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Payment Method — large card toggles */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400 mb-2">
            Payment Method
          </p>
          {canAccessElectronicPayment ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => handlePaymentMethodChange('Check')}
                className={`relative text-left p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === 'Check'
                    ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  <BanknotesIcon className={`h-6 w-6 flex-shrink-0 ${paymentMethod === 'Check' ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white">Check</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Mail check separately, optional check #</p>
                  </div>
                  {paymentMethod === 'Check' && (
                    <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={() => handlePaymentMethodChange('ACH')}
                className={`relative text-left p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === 'ACH'
                    ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  <BuildingLibraryIcon className={`h-6 w-6 flex-shrink-0 ${paymentMethod === 'ACH' ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white">ACH</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Direct bank transfer, processed instantly</p>
                  </div>
                  {paymentMethod === 'ACH' && (
                    <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>
              </button>
            </div>
          ) : (
            <div className="p-3 sm:p-4 rounded-xl border-2 border-primary bg-primary/5 dark:bg-primary/10">
              <div className="flex items-start gap-3">
                <BanknotesIcon className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Check</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Mail check separately, optional check #</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ACH Payment - Bank Account Selection */}
        {paymentMethod === 'ACH' && (
          <div>
            {loadingAccounts ? (
              <div className="flex items-center justify-center py-6 gap-3">
                <OathSpinner size="sm" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading bank accounts...</span>
              </div>
            ) : bankAccounts.length === 0 ? (
              <>
                <OathAlert variant="warning" className="mb-4">
                  No bank accounts found. Please add a bank account before submitting with ACH payment.
                </OathAlert>
                <a
                  href={`/app/admin?agencyId=${report?.agencyId}&tab=payment-accounts`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 whitespace-nowrap px-3 py-2 text-xs bg-primary hover:bg-primary-dark text-white no-underline"
                >
                  Manage Payment Accounts &rarr;
                </a>
              </>
            ) : (
              <div className="space-y-3">
                {/* NET + BUF account selectors side-by-side on wide viewports */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      NET Payment Account
                    </label>
                    <AccountSelect
                      value={netBankAccountId}
                      onChange={setNetBankAccountId}
                      accounts={bankAccounts}
                      currentUserId={currentUserId}
                      ariaLabel="NET payment account"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      BUF Payment Account
                    </label>
                    <AccountSelect
                      value={bufBankAccountId}
                      onChange={setBufBankAccountId}
                      accounts={bankAccounts}
                      currentUserId={currentUserId}
                      ariaLabel="BUF payment account"
                    />
                  </div>
                </div>

                {/* Footer line: agency context + refresh + manage link, all subtle */}
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    Accounts for <span className="font-medium text-gray-700 dark:text-gray-300">{report.agencyName}</span>
                    {refreshingAccounts && (
                      <span className="inline-flex items-center gap-1.5 ml-2">
                        <OathSpinner size="sm" />
                        refreshing…
                      </span>
                    )}
                  </span>
                  <a
                    href={`/app/admin?agencyId=${report?.agencyId}&tab=payment-accounts`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary dark:text-primary-light hover:underline font-medium no-underline"
                  >
                    Manage payment accounts &rarr;
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Check Payment — 4 inputs in one row on wide viewports (NET #, NET $, BUF #, BUF $) */}
        {paymentMethod === 'Check' && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  NET Check Number (Optional)
                </label>
                <input
                  type="text"
                  value={netCheckNumber}
                  onChange={(e) => setNetCheckNumber(e.target.value.toUpperCase())}
                  placeholder="Enter check number"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  NET Check Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    value={netCheckAmount}
                    onChange={(e) => setNetCheckAmount(e.target.value)}
                    onBlur={(e) => setNetCheckAmount(formatAmountOnBlur(e.target.value))}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  BUF Check Number (Optional)
                </label>
                {/* No BUF amount means there's no BUF check to track —
                    disable the field. Stale state is cleared by the effect
                    that watches bufCheckAmount. */}
                <input
                  type="text"
                  value={bufCheckNumber}
                  onChange={(e) => setBufCheckNumber(e.target.value.toUpperCase())}
                  disabled={!parseFloat(bufCheckAmount)}
                  placeholder={!parseFloat(bufCheckAmount) ? 'No BUF amount' : 'Enter check number'}
                  className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${!parseFloat(bufCheckAmount) ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  BUF Check Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="number"
                    value={bufCheckAmount}
                    onChange={(e) => setBufCheckAmount(e.target.value)}
                    onBlur={(e) => setBufCheckAmount(formatAmountOnBlur(e.target.value))}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
            </div>

            {/* Inline hint — subtle, not a chunky alert */}
            <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
              <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                {hasBatches
                  ? 'Amounts cover the report and selected batches combined. Use the same check number across NET and BUF if mailing one check per bucket. Checks should be mailed separately.'
                  : 'Check numbers are optional but speed up processing. Checks should be mailed separately.'}
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <OathAlert variant="error">
            {error}
          </OathAlert>
        )}
      </div>
    </OathModal>
  );
};

export default SubmitReportModal;
